/**
 * Sessions API service
 * Handles HTTP requests to the sessions endpoints
 */

import { api } from '@/lib/api';
import type {
  Session,
  SessionStatus,
  SessionsListResponse,
  SessionMessagesResponse,
  ServerlessSessionResponse,
  TutorTurnResponse,
} from '@/types/session';
import type { LiveSession, LiveSessionsResponse } from '@/types/coins';
import type { AnnotationCounts } from '@/lib/passageAnnotations';

/**
 * Fetch all sessions for the authenticated user
 * @param status - Optional filter by session status (active or completed)
 * @returns Promise resolving to array of Session objects
 */
export async function getSessions(status?: SessionStatus): Promise<Session[]> {
  const params: { status?: SessionStatus } = {};

  if (status) {
    params.status = status;
  }

  const response = await api.get<SessionsListResponse>('/sessions', { params });
  return response.data.sessions;
}

/**
 * Fetch session details with all messages for a specific session
 * @param sessionId - The session ID to fetch messages for
 * @returns Promise resolving to SessionMessagesResponse with passage, status, and messages
 */
export async function getSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
  const response = await api.get<SessionMessagesResponse>(`/sessions/${sessionId}/messages`);
  return response.data;
}

/**
 * Complete a session, marking it as finished
 * @param sessionId - The session ID to complete
 * @returns Promise resolving to the updated session
 */
export async function completeSession(
  sessionId: string
): Promise<{ sessionId: string; status: string }> {
  const response = await api.post<{ sessionId: string; status: string }>(
    `/sessions/${sessionId}/complete`
  );
  return response.data;
}

/**
 * Start a new serverless tutoring session
 * Creates a session with auto-generated passage and returns initial tutor greeting
 * @param preferredTopic - Optional topic preference for passage generation (e.g., "family gathering", "space exploration")
 * @returns Promise resolving to ServerlessSessionResponse with sessionId, passage, and tutorMessage
 */
export async function startServerlessSession(
  preferredTopic?: string
): Promise<ServerlessSessionResponse> {
  const body = preferredTopic ? { preferredTopic } : {};
  const response = await api.post<ServerlessSessionResponse>('/sessions/start', body);
  return response.data;
}

/**
 * Start a session FOR AN OWNED CHILD, called by the parent while she stays signed in as
 * herself. first-run-guide (T5).
 *
 * Posts { studentId }. The backend has accepted this since TS-016 and resolves the id
 * against the caller's own children, refusing anything else — the adopt-by-userId
 * capability that check replaced was deleted outright in BUG-20260721-185409, so this
 * neither relies on nor widens it.
 *
 * The session belongs to the CHILD: her wallet, her passage history, her pebbles, her
 * report. The parent is recorded as the paying parent on the record, which is what later
 * permits her to answer in it.
 *
 * ⚠️ Nothing is spent here. The balance is only checked at start; a session is charged on
 * completion, so a session started and never worked through costs the family nothing.
 *
 * (The parent self-trial that used to live here, posting { trial: true }, was removed on
 * 2026-07-27 — it drew from the same family wallet as the child's first session and had no
 * already-trialled guard anywhere.)
 *
 * @param studentId - userId of a child belonging to the calling parent
 * @returns Promise resolving to ServerlessSessionResponse with sessionId, passage, and tutorMessage
 */
export async function startChildSession(studentId: string): Promise<ServerlessSessionResponse> {
  const response = await api.post<ServerlessSessionResponse>('/sessions/start', {
    studentId,
  });
  return response.data;
}

/**
 * Submit student answer and receive tutor response in serverless mode
 * Synchronous request-response interaction (no polling needed)
 * @param sessionId - The session ID to submit answer to
 * @param answer - The student's answer text
 * @returns Promise resolving to TutorTurnResponse with tutor feedback, progress, and optional pebbles
 */
export async function submitServerlessAnswer(
  sessionId: string,
  answer: string,
  annotationCounts?: AnnotationCounts
): Promise<TutorTurnResponse> {
  const response = await api.post<TutorTurnResponse>(`/sessions/${sessionId}/respond`, {
    answer,
    // Passage annotation (Phase 1): the running tally of marks drawn on the passage, sent with
    // every turn so the latest wins. It rides this call rather than having one of its own — the
    // backend folds it into the session write it was already making. Counting only; nothing is
    // graded and nothing is gated on it.
    ...(annotationCounts ? { annotationCounts } : {}),
  });
  return response.data;
}

/**
 * Cancel a session, marking it as abandoned (Report Issue flow).
 *
 * Implements TS-018 T6 (CCM-02 coin-neutrality): this call sets status='cancelled'
 * without deducting or refunding any OtterCoin. The consumer (Report Issue UI, a
 * later US story) calls this before navigating away from the chat.
 *
 * On success, callers should invalidate React Query keys ['sessions'] and
 * ['session', sessionId, 'messages'] to reflect the freed active-session slot.
 *
 * @param sessionId - The session ID to cancel
 * @param cancelReason - Optional reason string (e.g. 'report_issue'); stored schemalessly
 * @returns Promise resolving to { sessionId, status: 'cancelled', updatedAt }
 */
export async function cancelSession(
  sessionId: string,
  cancelReason?: string
): Promise<{ sessionId: string; status: 'cancelled'; updatedAt: string }> {
  const body = cancelReason ? { cancelReason } : {};
  const response = await api.post<{ sessionId: string; status: 'cancelled'; updatedAt: string }>(
    `/sessions/${sessionId}/cancel`,
    body
  );
  return response.data;
}

/**
 * Force-end a live English tutoring session (parent-controlled).
 *
 * Implements TS-007 T2 (AC-TS007.3/4, API-001). Cloned from cancelSession above:
 * same POST /sessions/{sessionId}/{verb} route family, empty body, typed terminal
 * status response. Unlike cancelSession, this call charges 1 coin server-side
 * (empty-wallet/Unlimited succeed uncharged) and preserves earned pebbles.
 *
 * TO-LL-036: errors (401/403/404/409/500) are never caught here — they propagate
 * as AuthError {code, message} so useEndSession (T3) can surface them without
 * navigating away. 409 CONFLICT means the session was no longer active when the
 * request landed (e.g. it already completed or was ended by a concurrent request).
 *
 * @param sessionId - The live session ID to end
 * @returns Promise resolving to { sessionId, status: 'ended_by_parent', updatedAt }
 */
export async function endEnglishSession(
  sessionId: string
): Promise<{ sessionId: string; status: 'ended_by_parent'; updatedAt: string }> {
  const response = await api.post<{
    sessionId: string;
    status: 'ended_by_parent';
    updatedAt: string;
  }>(`/sessions/${sessionId}/end`, {});
  return response.data;
}

/**
 * List all currently-active English tutoring sessions across the parent's children
 * (Math Coach excluded, at most one per child).
 *
 * Implements TS-007 T2 (AC-TS007.4, API-002). Cloned from getSessions above: same
 * GET + unwrap-response.data pattern, applied to the parent-scoped listing endpoint.
 *
 * TO-LL-036: errors (401/403/500) are never caught here — they propagate to the
 * listing page's React Query error state (T9).
 * TO-LL-044: response unwrapped from the exact camelCase 'liveSessions' key.
 *
 * @returns Promise resolving to the array of LiveSession entries
 */
export async function getLiveSessions(): Promise<LiveSession[]> {
  const response = await api.get<LiveSessionsResponse>('/parent/live-sessions');
  return response.data.liveSessions;
}
